import { useState } from 'react'
import api from '../../utils/api'
import useToastContext from '../useToastContext'
import { notificationTypes } from '../../contexts/toastContext'

const useAmenitiesApi = () => {
  const addToast = useToastContext()

  const [amenitiesState, setAmenitiesState] = useState({
    amenities: [],
    isLoading: false,
    error: null,
  })

  const [amenityState, setAmenityState] = useState({
    amenity: {},
    isLoading: false,
    error: null,
  })

  const [createAmenityState, setCreateAmenityState] = useState({
    isLoading: false,
    error: null,
  })

  const [updateAmenityState, setUpdateAmenityState] = useState({
    isLoading: false,
    error: null,
  })

  const [deleteAmenityState, setDeleteAmenityState] = useState({
    isLoading: false,
    error: null,
  })

  const [amenityTypesState, setAmenityTypesState] = useState({
    amenityTypes: [],
    isLoading: false,
    error: null,
  })

  const [blackoutsState, setBlackoutsState] = useState({
    blackouts: [],
    isLoading: false,
    error: null,
  })

  // List endpoints return the response envelope { data: [], pagination: {...} }.
  // Single-row endpoints (getAmenity, createAmenity, updateAmenity, all
  // amenityType writes) return the bare row after Phase 1.
  async function getAmenities(replace = false, params) {
    try {
      setAmenitiesState((currentAmenitiesState) => ({
        ...currentAmenitiesState,
        isLoading: true,
        error: null,
      }))
      const response = await api.get('/amenities', {
        params,
        includeToken: true,
      })
      setAmenitiesState({
        amenities: replace
          ? response.data.data
          : [...amenitiesState.amenities, ...response.data.data],
        isLoading: false,
        error: null,
      })
      return response.data
    } catch (error) {
      setAmenitiesState((currentAmenitiesState) => ({
        ...currentAmenitiesState,
        isLoading: false,
        error: error.response?.data?.message || 'Error al obtener amenities',
      }))
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al obtener amenities'
      )
    }
  }

  async function getAmenity(amenityId) {
    try {
      setAmenityState((currentAmenityState) => ({
        ...currentAmenityState,
        isLoading: true,
        error: null,
      }))
      const response = await api.get(`/amenities/${amenityId}`, {
        includeToken: true,
      })
      // Bare row — backend no longer wraps in { data: row }.
      setAmenityState({
        amenity: response.data,
        isLoading: false,
        error: null,
      })
      return response.data
    } catch (error) {
      setAmenityState((currentAmenityState) => ({
        ...currentAmenityState,
        isLoading: false,
        error: error.response?.data?.message || 'Error al obtener amenity',
      }))
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al obtener amenity'
      )
    }
  }

  async function createAmenity(amenity) {
    try {
      setCreateAmenityState({ isLoading: true, error: null })
      const response = await api.post('/amenities', amenity, {
        includeToken: true,
      })
      setCreateAmenityState({ isLoading: false, error: null })
      addToast(notificationTypes.success, 'Amenity creado con éxito')
      // Bare row.
      return response.data
    } catch (error) {
      setCreateAmenityState({
        isLoading: false,
        error: error.response?.data?.message || 'Error al crear amenity',
      })
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al crear amenity'
      )
      throw error
    }
  }

  async function updateAmenity(amenity) {
    try {
      setUpdateAmenityState({ isLoading: true, error: null })
      const response = await api.put(`/amenities/${amenity.id}`, amenity, {
        includeToken: true,
      })
      setUpdateAmenityState({ isLoading: false, error: null })
      addToast(notificationTypes.success, 'Amenity actualizado con éxito')
      return response.data
    } catch (error) {
      setUpdateAmenityState({
        isLoading: false,
        error: error.response?.data?.message || 'Error al actualizar amenity',
      })
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al actualizar amenity'
      )
      throw error
    }
  }

  async function deleteAmenity(amenityId) {
    try {
      setDeleteAmenityState({ isLoading: true, error: null })
      await api.remove(`/amenities/${amenityId}`, { includeToken: true })
      setDeleteAmenityState({ isLoading: false, error: null })
      addToast(notificationTypes.success, 'Amenity eliminado con éxito')
      setAmenitiesState((currentState) => ({
        ...currentState,
        amenities: currentState.amenities.filter((a) => a.id !== amenityId),
      }))
    } catch (error) {
      setDeleteAmenityState({
        isLoading: false,
        error: error.response?.data?.message || 'Error al eliminar amenity',
      })
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al eliminar amenity'
      )
      throw error
    }
  }

  async function getAmenityTypes() {
    try {
      setAmenityTypesState((currentState) => ({
        ...currentState,
        isLoading: true,
        error: null,
      }))
      const response = await api.get('/amenityTypes', { includeToken: true })
      // Envelope shape.
      setAmenityTypesState({
        amenityTypes: response.data?.data || [],
        isLoading: false,
        error: null,
      })
      return response.data?.data || []
    } catch (error) {
      setAmenityTypesState((currentState) => ({
        ...currentState,
        isLoading: false,
        error:
          error.response?.data?.message ||
          'Error al obtener tipos de amenities',
      }))
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al obtener tipos de amenities'
      )
    }
  }

  async function createAmenityType(amenityType) {
    try {
      const response = await api.post('/amenityTypes', amenityType, {
        includeToken: true,
      })
      addToast(notificationTypes.success, 'Tipo de amenity creado con éxito')
      await getAmenityTypes()
      return response.data?.data || response.data
    } catch (error) {
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al crear tipo de amenity'
      )
      throw error
    }
  }

  async function updateAmenityType(id, payload) {
    try {
      const response = await api.put(`/amenityTypes/${id}`, payload, {
        includeToken: true,
      })
      addToast(notificationTypes.success, 'Tipo de amenity actualizado')
      await getAmenityTypes()
      return response.data?.data || response.data
    } catch (error) {
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al actualizar tipo de amenity'
      )
      throw error
    }
  }

  async function deleteAmenityType(amenityTypeId) {
    try {
      await api.remove(`/amenityTypes/${amenityTypeId}`, {
        includeToken: true,
      })
      addToast(notificationTypes.success, 'Tipo de amenity eliminado con éxito')
      await getAmenityTypes()
    } catch (error) {
      addToast(
        notificationTypes.error,
        error.response?.data?.message || 'Error al eliminar tipo de amenity'
      )
      throw error
    }
  }

  // ----- Blackouts -----
  async function getBlackouts(amenityId) {
    try {
      setBlackoutsState((currentState) => ({
        ...currentState,
        isLoading: true,
        error: null,
      }))
      const response = await api.get(`/amenities/${amenityId}/blackouts`, {
        includeToken: true,
      })
      setBlackoutsState({
        blackouts: response.data?.data || [],
        isLoading: false,
        error: null,
      })
      return response.data?.data || []
    } catch (error) {
      setBlackoutsState((currentState) => ({
        ...currentState,
        isLoading: false,
        error:
          error.response?.data?.message ||
          'Error al obtener fechas no disponibles',
      }))
      addToast(
        notificationTypes.error,
        error.response?.data?.message ||
          'Error al obtener fechas no disponibles'
      )
    }
  }

  async function createBlackout(amenityId, payload) {
    try {
      const response = await api.post(
        `/amenities/${amenityId}/blackouts`,
        payload,
        { includeToken: true }
      )
      addToast(notificationTypes.success, 'Fecha no disponible registrada')
      await getBlackouts(amenityId)
      return response.data
    } catch (error) {
      addToast(
        notificationTypes.error,
        error.response?.data?.message ||
          'Error al registrar fecha no disponible'
      )
      throw error
    }
  }

  async function deleteBlackout(blackoutId, amenityId) {
    try {
      await api.remove(`/blackouts/${blackoutId}`, { includeToken: true })
      addToast(notificationTypes.success, 'Fecha no disponible eliminada')
      if (amenityId) await getBlackouts(amenityId)
    } catch (error) {
      addToast(
        notificationTypes.error,
        error.response?.data?.message ||
          'Error al eliminar fecha no disponible'
      )
      throw error
    }
  }

  const state = {
    amenitiesState,
    amenityState,
    createAmenityState,
    updateAmenityState,
    deleteAmenityState,
    amenityTypesState,
    blackoutsState,
  }

  const methods = {
    getAmenities,
    getAmenity,
    createAmenity,
    updateAmenity,
    deleteAmenity,
    getAmenityTypes,
    createAmenityType,
    updateAmenityType,
    deleteAmenityType,
    getBlackouts,
    createBlackout,
    deleteBlackout,
  }

  return [state, methods]
}

export default useAmenitiesApi
